import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Loader from "../common/Loader.jsx";

// Context & Permissions
import { useGlobal } from "../../GlobalContext";
import { canEdit, canDelete } from "../common/permissionUtils";
import ReactPaginate from "react-paginate";
import DOMPurify from "dompurify";
import "./payment.css";
import * as paymentApi from "./payment-api";
import { sanitizeInput, sanitizeNumericInput } from "../common/security-utils";
import { FaCheckCircle } from "react-icons/fa";
import Payment from "../../assets/svg/payment-ic.svg";
import lar from "../../assets/svg/lar.svg";
import rar from "../../assets/svg/rar.svg";

const sanitize = (input) => {
  if (typeof input !== "string") return input;
  return DOMPurify.sanitize(input, { ALLOWED_TAGS: [] });
};

const PaymentView = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  // 🔒 Permissions
  const { rolePermissionList } = useGlobal();
  const hasEditPermission = canEdit(rolePermissionList, "Payment");
  const hasDeletePermission = canDelete(rolePermissionList, "Payment");

  const [isLoading, setIsLoading] = useState(false);
  const [filter, setFilter] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");

  // Pagination
  const [itemOffset, setItemOffset] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
const [pageCount, setPageCount] = useState(0);
  const [records, setRecords] = useState(0);

  // Stats data
  const [stats, setStats] = useState({
    totalPayment: 0,
    totalInvoices: 0,
    outstanding: 0,
    amountReceived: 0,
  });

  // Customer info
  const [customerInfo, setCustomerInfo] = useState({
    customerName: "—",
    revenue: 0,
    status: "Pending",
  });

  // Payment history data
  const [payments, setPayments] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  const formatCurrency = (amount) => {
    const num = Number.isFinite(Number(amount)) ? Number(amount) : 0;
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(num);
  };

  const formatCurrencyShort = (amount) => {
    const num = Number.isFinite(Number(amount)) ? Number(amount) : 0;
    if (num >= 10000000) {
      return `₹${(num / 10000000).toFixed(2)} Cr`;
    } else if (num >= 100000) {
      return `₹${(num / 100000).toFixed(2)} L`;
    } else if (num >= 1000) {
      return `₹${(num / 1000).toFixed(2)} K`;
    }
    return formatCurrency(num);
  };

  useEffect(() => {
    if (!id) return;

    let cancelled = false;

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const safeSearch = encodeURIComponent(sanitizeInput(searchQuery));
        console.log("🔍 Fetching payment view for ID:", id);
        const queryParams = `?status=${filter==="All" ? "" : filter}&search=${safeSearch}&limit=${itemsPerPage}&skip=${itemOffset * itemsPerPage}`;
        const response = await paymentApi.getPaymentViewDetailsApi(id, queryParams);
        console.log("📦 Payment view response:", response);
        const body = response?.data ?? {};

        if (cancelled) return;

        if (body.success) {
          console.log("✅ Success response received");
          // Extract customer info and payments from response
          // Response shape: { success, customer: { ...stats }, payments: [...] }
          // or: { success, data: { customer: {...}, payments: [...] } }
          const cust = body.customer ?? body.data?.customer ?? body.data ?? {};
          console.log("👤 Customer info:", cust);

          const paymentsList = Array.isArray(body.payments)
            ? body.payments
            : Array.isArray(body.data?.payments)
              ? body.data.payments
              : Array.isArray(body.payment_history)
                ? body.payment_history
                : Array.isArray(cust?.payments)
                  ? cust.payments
                  : [];
          console.log("💳 Payments list:", paymentsList);

          // Set customer info
          setCustomerInfo({
            customerName:
              sanitizeInput(cust?.customer_name ?? cust?.customer ?? "—") ||
              "—",
            revenue: Number.isFinite(
              Number(cust?.annual_revenue ?? cust?.revenue ?? 0)
            )
              ? Number(cust?.annual_revenue ?? cust?.revenue ?? 0)
              : 0,
            status:
              sanitizeInput(cust?.payment_status ?? cust?.status ?? "Pending") ||
              "Pending",
          });

          // Set stats
          setStats({
            totalPayment: Number.isFinite(
              Number(cust?.total_payment ?? cust?.total_amount ?? 0)
            )
              ? Number(cust?.total_payment ?? cust?.total_amount ?? 0)
              : 0,
            totalInvoices: Number.isFinite(
              Number(cust?.invoice_count ?? cust?.total_invoices ?? 0)
            )
              ? Number(cust?.invoice_count ?? cust?.total_invoices ?? 0)
              : 0,
            outstanding: Number.isFinite(
              Number(cust?.outstanding ?? cust?.pending_amount ?? 0)
            )
              ? Number(cust?.outstanding ?? cust?.pending_amount ?? 0)
              : 0,
            amountReceived: Number.isFinite(
              Number(cust?.amount_received ?? cust?.paid_amount ?? 0)
            )
              ? Number(cust?.amount_received ?? cust?.paid_amount ?? 0)
              : 0,
          });

          // Format and set payments
          const formatted = paymentsList.map((payment) => ({
            id: sanitizeInput(payment.payment_id ?? payment.id ?? ""),
            date: sanitizeInput(payment.payment_date ?? payment.date ?? ""),
            invoice: sanitizeInput(payment.invoice_no ?? payment.invoice ?? ""),
            invoiceDate: sanitizeInput(payment.invoice_date ?? ""),
            totalAmount: sanitizeNumericInput(
              payment.amount ?? payment.total_amount ?? 0
            ),
            paid: sanitizeNumericInput(payment.paid ?? 0),
            tds_amount: sanitizeNumericInput(payment.tds_amount ?? 0),
            outstanding: sanitizeNumericInput(
              payment.outstanding_amount ?? payment.outstanding ?? 0
            ),
            paymentType: sanitizeInput(
              payment.payment_mode ?? payment.payment_type ?? "—"
            ),
            status: sanitizeInput(payment.status ?? "Pending"),
          }));

          if (!cancelled) {
            setPayments(formatted);
            setTotalCount(response.data.total_rec);
          }
           setPageCount(response.data.total_pgs+1 ?? 0);
        setRecords(response.data.total_rec);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Error fetching payment data:", error);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [id, itemOffset, itemsPerPage, searchQuery, filter]);

  // Server handles all filtering (status, search, pagination)
  // No client-side filtering needed since API returns filtered results
  const filteredPayments = payments;

  const handlePageClick = useCallback((event) => {
    setItemOffset(event.selected);
  }, []);

  // Reset to first page when filter or search changes
  useEffect(() => {
    setItemOffset(0);
  }, [filter, searchQuery]);

  const getStatusColor = (status) => {
    const st = (status || "").toLowerCase();
    if (st === "paid") {
      return {
        bg: "#ECFDF5",
        color: "#059669",
        border: "1px solid #D1FAE5",
      };
    } else if (st === "partially" || st === "partially paid") {
      return {
        bg: "#FEF3C7",
        color: "#92400E",
        border: "1px solid #FCD34D",
      };
    } else {
      return {
        bg: "#FEE2E2",
        color: "#991B1B",
        border: "1px solid #FECACA",
      };
    }
  };

  const getStatusBadgeColor = (status) => {
    const st = (status || "").toLowerCase();
    if (st === "paid") return "#ECFDF5";
    if (st === "partially" || st === "partially paid") return "#FEF3C7";
    return "#FEE2E2";
  };

  return (
    <div className="payment-view-page">
      <div className="payment-view-top-header">
        {/* Header */}
        <div className="payment-view-header">
          <div className="d-flex align-items-center gap-3">
            <div
              className="back-icon"
              onClick={() => navigate(-1)}
              style={{ cursor: "pointer" }}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15 18L9 12L15 6"
                  stroke="#1E1E1E"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            <div className="d-flex align-items-center gap-3">
              <div
                className="company-logo"
                style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "50%",
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  fontSize: "20px",
                  fontWeight: "600",
                }}
              >
                {(customerInfo.customerName || "?")
                  .slice(0, 2)
                  .toUpperCase()}
              </div>
              <div>
                <div
                  className="company-name"
                  style={{
                    fontSize: "20px",
                    fontWeight: "600",
                    color: "#1E1E1E",
                  }}
                >
                  {customerInfo.customerName}
                </div>
                <div style={{ fontSize: "14px", color: "#8790A1" }}>
                  Revenue: {formatCurrencyShort(customerInfo.revenue)}
                </div>
              </div>
            </div>
          </div>

          <div className="d-flex align-items-center gap-3">

            <div
              className="status-badge"
              style={{
                background: getStatusBadgeColor(customerInfo.status),
                color: "#172B4D",
                padding: "6px 16px",
                borderRadius: "100px",
                fontSize: "15px",
                fontWeight: "500",
              }}
            >
              {customerInfo.status}
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="payment-stats-section">
          <div className="row g-3">
            <div className="col-12 col-md-6 col-lg-3">
              <div
                className="stat-card"
                style={{
                  background: "#F8F8F8",
                  borderRadius: "16px",
                  padding: "20px",
                }}
              >
                <div
                  style={{
                    fontSize: "11px",
                    color: "#8790A1",
                    marginBottom: "8px",
                    textTransform: "uppercase",
                  }}
                >
                  TOTAL PAYMENT
                </div>
                <div
                  style={{
                    fontSize: "16px",
                    fontWeight: "600",
                    color: "#21272C",
                  }}
                >
                  {formatCurrencyShort(stats.totalPayment)}
                </div>
              </div>
            </div>

            <div className="col-12 col-md-6 col-lg-3">
              <div
                className="stat-card"
                style={{
                  background: "#F8F8F8",
                  borderRadius: "16px",
                  padding: "20px",
                }}
              >
                <div
                  style={{
                    fontSize: "11px",
                    color: "#8790A1",
                    marginBottom: "8px",
                    textTransform: "uppercase",
                  }}
                >
                  TOTAL INVOICES
                </div>
                <div
                  style={{
                    fontSize: "16px",
                    fontWeight: "600",
                    color: "#21272C",
                  }}
                >
                  {stats.totalInvoices}
                </div>
              </div>
            </div>

            <div className="col-12 col-md-6 col-lg-3">
              <div
                className="stat-card"
                style={{
                  background: "#F8F8F8",
                  borderRadius: "16px",
                  padding: "20px",
                }}
              >
                <div
                  style={{
                    fontSize: "11px",
                    color: "#8790A1",
                    marginBottom: "8px",
                    textTransform: "uppercase",
                  }}
                >
                  OUTSTANDING
                </div>
                <div
                  style={{
                    fontSize: "16px",
                    fontWeight: "600",
                    color: "#21272C",
                  }}
                >
                  {formatCurrencyShort(stats.outstanding)}
                </div>
              </div>
            </div>

            <div className="col-12 col-md-6 col-lg-3">
              <div
                className="stat-card"
                style={{
                  background: "#F8F8F8",
                  borderRadius: "16px",
                  padding: "20px",
                }}
              >
                <div
                  style={{
                    fontSize: "11px",
                    color: "#8790A1",
                    marginBottom: "8px",
                    textTransform: "uppercase",
                  }}
                >
                  AMOUNT RECEIVED
                </div>
                <div
                  style={{
                    fontSize: "16px",
                    fontWeight: "600",
                    color: "#21272C",
                  }}
                >
                  {formatCurrencyShort(stats.amountReceived)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment History Section */}
      <div className="payment-history-section" style={{ marginTop: "30px" }}>
        <div className="payment-card" style={{ padding: "24px" }}>
          {/* <div className="d-flex justify-content-between align-items-center mb-4">
            
          </div> */}

          {/* Filters and Search */}
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
            <div className="d-flex gap-2 flex-wrap"><h3
              style={{
                fontSize: "16px",
                fontWeight: "700",
                color: "#21272C",
                margin: 0,
              }}
            >
              Payment History — {totalCount}
            </h3>

             
            </div>
<div className="d-flex gap-2 flex-wrap">
   {["All", "Paid", "Partially","Due","Overdue","Cancelled"].map((filterOption) => (
                <button
                  key={filterOption}
                  onClick={() => setFilter(filterOption)}
                  style={{
                    background:
                      filter === filterOption ? "#1E1E1E" : "transparent",
                    color: filter === filterOption ? "white" : "#1E1E1E",
                    border:
                      filter === filterOption ? "none" : "0.6px solid #D4DDE7",
                    borderRadius: "20px",
                    padding: "8px 20px",
                    fontSize: "13px",
                    fontWeight: "400",
                    cursor: "pointer",
                    transition: "all 0.3s",
                  }}
                >
                  {filterOption}
                </button>
              ))}
            <div className="search-box gap-2" style={{ position: "relative" }}>
              
              <input
                type="text"
                placeholder="Search..."
                className="no-focus-border"
                value={searchQuery}
                onChange={(e) => setSearchQuery(sanitize(e.target.value))}
                style={{
                  paddingLeft: "16px",
                  paddingRight: "16px",
                  paddingTop: "6px",
                  paddingBottom: "6px",
                  borderRadius: "55px",
                  fontSize: "14px",
                  width: "250px",
                  maxWidth: "100%",
                  outline: "none",
                  background: "#F5F7F9",
                  border: "none",
                }}
              />
            </div>
          </div>
</div>
          {/* Table */}
          {isLoading ? (
            <div className="text-center py-5">
              <Loader height="150px" />
            </div>
          ) : (
            <div className="table-responsive">
              <table
                className="payment-table"
                style={{ borderCollapse: "separate", borderSpacing: "0" }}
              >
                <thead>
                  <tr style={{ border: "1px solid #D5DDE7" }}>
                    <th
                      style={{
                        fontSize: "13px",
                        color: "#121417",
                        fontWeight: "500",
                        padding: "12px",
                        textTransform: "uppercase",
                        borderTop: "1px solid #D5DDE7",
                        borderBottom: "1px solid #D5DDE7",
                        borderRight: "0.4px solid #D5DDE7",
                      }}
                    >
                      Payment ID
                    </th>
                    <th
                      style={{
                        fontSize: "13px",
                        color: "#121417",
                        fontWeight: "500",
                        padding: "12px",
                        textTransform: "uppercase",
                        borderTop: "1px solid #D5DDE7",
                        borderBottom: "1px solid #D5DDE7",
                        borderRight: "0.4px solid #D5DDE7",
                      }}
                    >
                      Invoice
                    </th>
                    <th
                      style={{
                        fontSize: "13px",
                        color: "#121417",
                        fontWeight: "500",
                        padding: "12px",
                        textTransform: "uppercase",
                        borderTop: "1px solid #D5DDE7",
                        borderBottom: "1px solid #D5DDE7",
                        borderRight: "0.4px solid #D5DDE7",
                      }}
                    >
                      Invoice Date
                    </th>
                    <th
                      style={{
                        fontSize: "13px",
                        color: "#121417",
                        fontWeight: "500",
                        padding: "12px",
                        textTransform: "uppercase",
                        borderTop: "1px solid #D5DDE7",
                        borderBottom: "1px solid #D5DDE7",
                        borderRight: "0.4px solid #D5DDE7",
                      }}
                    >
                      Total Amount
                    </th>
                    <th
                      style={{
                        fontSize: "13px",
                        color: "#121417",
                        fontWeight: "500",
                        padding: "12px",
                        textTransform: "uppercase",
                        borderTop: "1px solid #D5DDE7",
                        borderBottom: "1px solid #D5DDE7",
                        borderRight: "0.4px solid #D5DDE7",
                      }}
                    >
                      Paid
                    </th>
                     <th
                      style={{
                        fontSize: "13px",
                        color: "#121417",
                        fontWeight: "500",
                        padding: "12px",
                        textTransform: "uppercase",
                        borderTop: "1px solid #D5DDE7",
                        borderBottom: "1px solid #D5DDE7",
                        borderRight: "0.4px solid #D5DDE7",
                      }}
                    >
                      TDS
                    </th>
                    <th
                      style={{
                        fontSize: "13px",
                        color: "#121417",
                        fontWeight: "500",
                        padding: "12px",
                        textTransform: "uppercase",
                        borderTop: "1px solid #D5DDE7",
                        borderBottom: "1px solid #D5DDE7",
                        borderRight: "0.4px solid #D5DDE7",
                      }}
                    >
                      Outstanding
                    </th>
                    <th
                      style={{
                        fontSize: "13px",
                        color: "#121417",
                        fontWeight: "500",
                        padding: "12px",
                        textTransform: "uppercase",
                        borderTop: "1px solid #D5DDE7",
                        borderBottom: "1px solid #D5DDE7",
                        borderRight: "0.4px solid #D5DDE7",
                      }}
                    >
                      Payment Type
                    </th>
                    <th
                      style={{
                        fontSize: "13px",
                        color: "#121417",
                        fontWeight: "500",
                        padding: "12px",
                        textTransform: "uppercase",
                        borderTop: "1px solid #D5DDE7",
                        borderBottom: "1px solid #D5DDE7",
                      }}
                    >
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPayments.length === 0 ? (
                    <tr>
                      <td
                        colSpan="7"
                        className="text-center"
                        style={{ padding: "40px", color: "#8790A1" }}
                      >
                        No payments found
                      </td>
                    </tr>
                  ) : (
                    filteredPayments.map((payment, index) => {
                      const statusColor = getStatusColor(payment.status);
                      return (
                        <tr
                          key={index}
                          style={{
                            cursor: "pointer",
                            transition: "background 0.2s",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "#F9FAFB";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "white";
                          }}
                        >
                          <td
                            style={{
                              padding: "16px",
                              borderBottom: "1px solid #EBEBEB",
                            }}
                          >
                            <div className="d-flex align-items-center gap-3">
                              <div
                                style={{
                                  width: "40px",
                                  height: "40px",
                                  borderRadius: "100px",
                                  background: "#EEEDFF",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <img src={Payment} alt="payment" />
                              </div>
                              <div>
                                <div
                                  style={{
                                    fontSize: "14px",
                                    fontWeight: "400",
                                    color: "#21272C",
                                  }}
                                >
                                  {payment.id || "—"}
                                </div>
                                <div
                                  style={{
                                    fontSize: "12.5px",
                                    color: "#8790A1",
                                    fontWeight: "400",
                                    marginTop: "2px",
                                  }}
                                >
                                  {payment.date || "—"}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td
                            style={{
                              padding: "16px",
                              fontSize: "14px",
                              fontWeight: "500",
                              color: "#21272C",
                              borderBottom: "1px solid #EBEBEB",
                            }}
                          >
                            {payment.invoice || "—"}
                          </td>
                          <td
                            style={{
                              padding: "16px",
                              fontSize: "14px",
                              fontWeight: "500",
                              color: "#21272C",
                              borderBottom: "1px solid #EBEBEB",
                            }}
                          >
                            {payment.invoiceDate || "—"}
                          </td>
                          <td
                            style={{
                              padding: "16px",
                              fontSize: "14px",
                              color: "#21272C",
                              fontWeight: "500",
                              borderBottom: "1px solid #EBEBEB",
                            }}
                          >
                            {formatCurrency(payment.totalAmount)}
                          </td>
                          <td
                            style={{
                              padding: "16px",
                              fontSize: "14px",
                              color: "#17B65B",
                              fontWeight: "500",
                              borderBottom: "1px solid #EBEBEB",
                            }}
                          >
                            {formatCurrency(payment.paid)}
                          </td>
                          <td
                            style={{
                              padding: "16px",
                              fontSize: "14px",
                              color: "#17B65B",
                              fontWeight: "500",
                              borderBottom: "1px solid #EBEBEB",
                            }}
                          >
                            {formatCurrency(payment.tds_amount)}
                          </td>
                          <td
                            style={{
                              padding: "16px",
                              fontSize: "14px",
                              color:
                                Number(payment.outstanding) > 0
                                  ? "#EF4444"
                                  : "#21272C",
                              fontWeight: "500",
                              borderBottom: "1px solid #EBEBEB",
                            }}
                          >
                            {formatCurrency(payment.outstanding)}
                          </td>
                          <td
                            style={{
                              padding: "16px",
                              fontSize: "14px",
                              color: "#21272C",
                              fontWeight: "500",
                              borderBottom: "1px solid #EBEBEB",
                            }}
                          >
                            {payment.paymentType}
                          </td>
                          <td
                            style={{
                              padding: "16px",
                              borderBottom: "1px solid #EBEBEB",
                            }}
                          >
                            {payment.status === "Paid" ||
                            payment.status === "paid" ? (
                              <div
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "6px",
                                  background: statusColor.bg,
                                  color: statusColor.color,
                                  padding: "4px 12px",
                                  borderRadius: "50px",
                                  fontSize: "13px",
                                  fontWeight: "500",
                                  border: statusColor.border,
                                }}
                              >
                                <FaCheckCircle size={14} />
                                Paid
                              </div>
                            ) : payment.status === "Partially" ||
                              payment.status === "partially" ||
                              payment.status === "Partially Completed" ? (
                              <div
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  background: statusColor.bg,
                                  color: statusColor.color,
                                  padding: "4px 12px",
                                  borderRadius: "50px",
                                  fontSize: "13px",
                                  fontWeight: "500",
                                  border: statusColor.border,
                                }}
                              >
                                Partially
                              </div>
                            ) : (
                              <div
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  background: statusColor.bg,
                                  color: statusColor.color,
                                  padding: "4px 12px",
                                  borderRadius: "6px",
                                  fontSize: "13px",
                                  fontWeight: "500",
                                  border: statusColor.border,
                                }}
                              >
                                {payment.status || "Pending"}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination Footer */}
          {filteredPayments.length > 0 && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: "20px",
                paddingTop: "20px",
                borderTop: "1px solid #E5E7EB",
              }}
            >
              {/* Items Per Page Selector */}
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <label
                  style={{
                    fontSize: "13px",
                    color: "#8790A1",
                    fontWeight: "500",
                  }}
                >
                  Show
                </label>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setItemOffset(0);
                  }}
                  style={{
                    padding: "6px 12px",
                    borderRadius: "6px",
                    border: "1px solid #D5DDE7",
                    fontSize: "13px",
                    backgroundColor: "white",
                    cursor: "pointer",
                    color: "#21272C",
                  }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
                <label
                  style={{
                    fontSize: "13px",
                    color: "#8790A1",
                    fontWeight: "500",
                  }}
                >
                  entries
                </label>
              </div>

              {/* Pagination Controls */}
              {pageCount > 1 && (
                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <ReactPaginate
                    previousLabel={
                      <img
                        src={lar}
                        alt="previous"
                        style={{ width: "20px", height: "20px" }}
                      />
                    }
                    nextLabel={
                      <img
                        src={rar}
                        alt="next"
                        style={{ width: "20px", height: "20px" }}
                      />
                    }
                    breakLabel="..."
                    pageCount={pageCount}
                    marginPagesDisplayed={2}
                    pageRangeDisplayed={3}
                    onPageChange={handlePageClick}
                    forcePage={itemOffset}
                    containerClassName="pagination"
                    activeClassName="active"
                    style={{
                      display: "flex",
                      gap: "4px",
                      listStyle: "none",
                      padding: 0,
                      margin: 0,
                    }}
                  />
                </div>
              )}

              {/* Record Count */}
              <div
                style={{
                  fontSize: "12px",
                  color: "#666",
                  textAlign: "right",
                }}
              >
                Total: {records} record{records !== 1 ? "s" : ""}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentView;
